import { useState } from "react";
import { FileText, Plus, Trash2, Edit2, AlertTriangle, Check, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatusBadge = (status) => {
  switch (status) {
    case 'abgelaufen':
      return <span className="badge bg-red-100 text-red-700 border-red-200">Abgelaufen</span>;
    case 'laeuft_bald_ab':
      return <span className="badge bg-amber-100 text-amber-700 border-amber-200">Läuft bald ab</span>;
    case 'unbefristet':
      return <span className="badge bg-blue-100 text-blue-700 border-blue-200">Unbefristet</span>;
    default:
      return <span className="badge bg-green-100 text-green-700 border-green-200">Aktiv</span>;
  }
};

const KonzessionenTab = ({ konzessionen, konzessionsarten, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    art: "", name: "", behoerde: "", aktenzeichen: "", erteilt_am: "",
    gueltig_bis: "", kosten: "", notizen: "", veranstaltung_name: "",
    veranstaltung_ort: "", veranstaltung_von: "", veranstaltung_bis: ""
  });

  const isVeranstaltung = form.art === 'SONDER_VERANSTALTUNG';

  const openDialog = (konzession = null) => {
    if (konzession) {
      setEditing(konzession);
      setForm({
        art: konzession.art || "", name: konzession.name || "",
        behoerde: konzession.behoerde || "", aktenzeichen: konzession.aktenzeichen || "",
        erteilt_am: konzession.erteilt_am || "", gueltig_bis: konzession.gueltig_bis || "",
        kosten: konzession.kosten?.toString() || "", notizen: konzession.notizen || "",
        veranstaltung_name: konzession.veranstaltung_name || "",
        veranstaltung_ort: konzession.veranstaltung_ort || "",
        veranstaltung_von: konzession.veranstaltung_von || "",
        veranstaltung_bis: konzession.veranstaltung_bis || ""
      });
    } else {
      setEditing(null);
      setForm({
        art: "", name: "", behoerde: "", aktenzeichen: "", erteilt_am: "",
        gueltig_bis: "", kosten: "", notizen: "", veranstaltung_name: "",
        veranstaltung_ort: "", veranstaltung_von: "", veranstaltung_bis: ""
      });
    }
    setDialogOpen(true);
  };

  const handleArtChange = (art) => {
    const selected = konzessionsarten.find(k => k.code === art);
    setForm({ ...form, art, name: selected?.name || "", behoerde: selected?.behoerde || "" });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.art || !form.name) {
      toast.error("Bitte Art und Name angeben");
      return;
    }
    try {
      const payload = { ...form, kosten: form.kosten ? parseFloat(form.kosten) : null };
      if (editing) {
        await axios.put(`${API}/konzessionen/${editing.id}`, payload, { withCredentials: true });
        toast.success("Konzession aktualisiert");
      } else {
        await axios.post(`${API}/konzessionen`, payload, { withCredentials: true });
        toast.success("Konzession erstellt");
      }
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Konzession wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/konzessionen/${id}`, { withCredentials: true });
      toast.success("Konzession gelöscht");
      onRefresh();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-base font-semibold text-[#3A4D39]">Genehmigungen & Konzessionen</h3>
          <p className="text-xs text-[#858585]">Gaststättenerlaubnis, Sonderkonzessionen, Veranstaltungen</p>
        </div>
        <Button onClick={() => openDialog()} className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm text-xs">
          <Plus size={14} className="mr-1" />Neue Konzession
        </Button>
      </div>

      {konzessionen.filter(k => k.status === 'abgelaufen' || k.status === 'laeuft_bald_ab').length > 0 && (
        <div className="bg-amber-50 border border-amber-200 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Achtung: Konzessionen prüfen!</p>
              <p className="text-xs text-amber-700">
                {konzessionen.filter(k => k.status === 'abgelaufen').length > 0 && `${konzessionen.filter(k => k.status === 'abgelaufen').length} abgelaufen. `}
                {konzessionen.filter(k => k.status === 'laeuft_bald_ab').length > 0 && `${konzessionen.filter(k => k.status === 'laeuft_bald_ab').length} laufen bald ab.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {konzessionen.length === 0 ? (
        <div className="bg-white border border-stone-200 p-8 text-center">
          <FileText size={48} className="mx-auto text-stone-300 mb-3" />
          <p className="text-[#5C5C5C]">Keine Konzessionen erfasst</p>
          <p className="text-xs text-[#858585] mt-1">Fügen Sie Ihre Gaststättenerlaubnis oder Sonderkonzessionen hinzu</p>
        </div>
      ) : (
        <div className="space-y-3">
          {konzessionen.map((k) => (
            <div key={k.id} className="bg-white border border-stone-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm">{k.name}</h4>
                    {getStatusBadge(k.status)}
                  </div>
                  <p className="text-xs text-[#858585]">{k.behoerde}</p>
                  {k.aktenzeichen && <p className="text-xs text-[#5C5C5C]">Aktenzeichen: {k.aktenzeichen}</p>}
                  {k.gueltig_bis && <p className="text-xs text-[#5C5C5C]">Gültig bis: {k.gueltig_bis}</p>}
                  {k.veranstaltung_name && (
                    <p className="text-xs text-[#3A4D39] mt-1">
                      <Calendar size={12} className="inline mr-1" />
                      {k.veranstaltung_name}: {k.veranstaltung_von} - {k.veranstaltung_bis}
                    </p>
                  )}
                </div>
                <div className="flex gap-1">
                  <button className="btn-icon p-1" onClick={() => openDialog(k)}><Edit2 size={14} /></button>
                  <button className="btn-icon danger p-1" onClick={() => handleDelete(k.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-stone-50 border border-stone-200 p-4 mt-6">
        <h4 className="font-medium text-sm mb-2">Konzessionsarten für Gastronomie:</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          {konzessionsarten.map((k) => (
            <div key={k.code} className="flex items-start gap-2">
              <div className={`w-2 h-2 rounded-full mt-1 ${k.befristet ? 'bg-amber-400' : 'bg-green-400'}`}></div>
              <div>
                <p className="font-medium">{k.name}</p>
                <p className="text-[#858585]">{k.beschreibung}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Konzession Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">{editing ? "Konzession bearbeiten" : "Neue Konzession"}</DialogTitle>
            <DialogDescription className="text-xs text-[#858585]">
              Erfassen Sie Ihre Gaststättenerlaubnis oder Sonderkonzession
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Art der Konzession *</Label>
                <Select value={form.art} onValueChange={handleArtChange}>
                  <SelectTrigger className="border-stone-300 rounded-sm"><SelectValue placeholder="Art wählen" /></SelectTrigger>
                  <SelectContent>
                    {konzessionsarten.map((k) => (
                      <SelectItem key={k.code} value={k.code}>{k.name} {k.befristet && "(befristet)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Name/Bezeichnung *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="border-stone-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Behörde</Label>
                <Input value={form.behoerde} onChange={(e) => setForm({...form, behoerde: e.target.value})} className="border-stone-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Aktenzeichen</Label>
                <Input value={form.aktenzeichen} onChange={(e) => setForm({...form, aktenzeichen: e.target.value})} className="border-stone-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Erteilt am</Label>
                <Input type="date" value={form.erteilt_am} onChange={(e) => setForm({...form, erteilt_am: e.target.value})} className="border-stone-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Gültig bis</Label>
                <Input type="date" value={form.gueltig_bis} onChange={(e) => setForm({...form, gueltig_bis: e.target.value})} className="border-stone-300 rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Kosten (€)</Label>
                <Input type="number" step="0.01" value={form.kosten} onChange={(e) => setForm({...form, kosten: e.target.value})} className="border-stone-300 rounded-sm font-mono" />
              </div>

              {isVeranstaltung && (
                <>
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <p className="text-xs uppercase tracking-wide text-[#3A4D39] font-medium mb-3">Veranstaltungsdetails</p>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Veranstaltungsname</Label>
                    <Input value={form.veranstaltung_name} onChange={(e) => setForm({...form, veranstaltung_name: e.target.value})} placeholder="z.B. Sommerfest 2024" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Veranstaltungsort</Label>
                    <Input value={form.veranstaltung_ort} onChange={(e) => setForm({...form, veranstaltung_ort: e.target.value})} placeholder="z.B. Marktplatz München" className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Von</Label>
                    <Input type="date" value={form.veranstaltung_von} onChange={(e) => setForm({...form, veranstaltung_von: e.target.value})} className="border-stone-300 rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Bis</Label>
                    <Input type="date" value={form.veranstaltung_bis} onChange={(e) => setForm({...form, veranstaltung_bis: e.target.value})} className="border-stone-300 rounded-sm" />
                  </div>
                </>
              )}

              <div className="col-span-2 space-y-2">
                <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Notizen</Label>
                <Textarea value={form.notizen} onChange={(e) => setForm({...form, notizen: e.target.value})} rows={2} className="border-stone-300 rounded-sm resize-none" />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Abbrechen</Button>
              <Button type="submit" className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm">
                <Check size={14} className="mr-1" />{editing ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KonzessionenTab;
